import React from 'react';
import {
  Box, ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel,
  Typography, Paper, Divider, Tooltip, Button, Chip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  COMPACT_OPTIONAL_BLOCKS, OPTIONAL_ORDER, FONT_SCALES, PAGE_CHOICES,
  DENSITIES, DENSITY_ORDER, optionalBlockRows, estimateRows, projectInfoRows, softwareRows,
} from '../../config/compactCV';

/**
 * Kontrollpanel för kompakt läge. Ligger alltid i .no-print.
 */
export default function CompactControls({
  mode, onModeChange, settings, onSettingsChange, persons, fit, showModeToggle = true,
}) {
  const set = patch => onSettingsChange({ ...settings, ...patch, autoNote: undefined });

  // Ett tillval som kryssas i för hand låses, så att auto-anpassningen inte
  // kryssar bort det igen i det tysta.
  const pin = (key, on) => {
    const rest = (settings.pinned || []).filter(k => k !== key);
    return on ? [...rest, key] : rest;
  };

  const toggleOptional = id => {
    const on = !settings.optional.includes(id);
    const next = on ? [...settings.optional, id] : settings.optional.filter(x => x !== id);
    set({ optional: OPTIONAL_ORDER.filter(x => next.includes(x)), pinned: pin(id, on) });
  };

  const ingressRows = persons.reduce(
    (sum, p) => sum + estimateRows(p.ingress, settings.fontScale), 0,
  );

  const expandedCount = persons.reduce(
    (sum, p) => sum + p.assignments.filter(a => a.expanded).length, 0,
  );

  /** Nästa tillval som skulle rymmas i det lediga utrymmet. */
  const suggestion = (() => {
    if (mode !== 'compact' || fit.overflowRows > 0) return null;
    for (const id of OPTIONAL_ORDER) {
      if (settings.optional.includes(id)) continue;
      const cost = optionalBlockRows(id, persons, settings);
      if (cost > 0 && cost <= fit.freeRows) {
        return { id, cost, label: COMPACT_OPTIONAL_BLOCKS.find(b => b.id === id).label };
      }
    }
    return null;
  })();

  return (
    <Paper className="no-print" variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {(showModeToggle || mode === 'compact') && (
          <Box>
            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
              Layout
            </Typography>
            {showModeToggle && (
              <ToggleButtonGroup size="small" exclusive value={mode}
                onChange={(e, v) => v && onModeChange(v)} sx={{ mb: 1, display: 'flex' }}>
                <ToggleButton value="full">Fullständig</ToggleButton>
                <ToggleButton value="compact">Kompakt</ToggleButton>
              </ToggleButtonGroup>
            )}
            {mode === 'compact' && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Får omfatta
                </Typography>
                <ToggleButtonGroup size="small" exclusive value={settings.pages}
                  onChange={(e, v) => v && set({ pages: v })} sx={{ display: 'flex' }}>
                  {PAGE_CHOICES.map(n => (
                    <ToggleButton key={n} value={n}>{n} sid</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}
          </Box>
        )}

        {mode === 'compact' && (
          <>
            <Divider orientation="vertical" flexItem />

            <Box sx={{ minWidth: 260 }}>
              <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                Tillval – i mån av plats
              </Typography>

              <OptionRow
                checked={settings.showIngress}
                onChange={() => set({
                  showIngress: !settings.showIngress,
                  pinned: pin('ingress', !settings.showIngress),
                })}
                label="Kort presentation"
                cost={ingressRows}
              />
              {COMPACT_OPTIONAL_BLOCKS.map(block => (
                <OptionRow
                  key={block.id}
                  checked={settings.optional.includes(block.id)}
                  onChange={() => toggleOptional(block.id)}
                  label={block.label}
                  cost={optionalBlockRows(block.id, persons, settings)}
                />
              ))}
              <Tooltip title="Projektbeskrivningen visas före rollbeskrivningen i utfällda uppdrag, med förleden Projektet: och Uppdraget:. Klicka en projektrad för att fälla ut den.">
                <Box>
                  <OptionRow
                    checked={settings.showProjectInfo}
                    onChange={() => set({ showProjectInfo: !settings.showProjectInfo })}
                    label="Ta med projektinfo"
                    cost={projectInfoRows(persons, settings)}
                  />
                </Box>
              </Tooltip>
              <Tooltip title="Mjukvarorna som kryssats i på uppdraget visas som etiketter under texten i utfällda uppdrag.">
                <Box>
                  <OptionRow
                    checked={settings.showSoftware}
                    onChange={() => set({ showSoftware: !settings.showSoftware })}
                    label="Ta med mjukvaror"
                    cost={softwareRows(persons)}
                  />
                </Box>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ minWidth: 250 }}>
              <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                Finjustering
              </Typography>

              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Textstorlek (pt)
              </Typography>
              <ToggleButtonGroup size="small" exclusive value={settings.fontScale}
                onChange={(e, v) => v && set({ fontScale: v, autoFit: false })}
                sx={{ mb: 1, display: 'flex' }}>
                {FONT_SCALES.map(f => (
                  <ToggleButton key={f.value} value={f.value} sx={{ px: 1 }}>
                    {f.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Täthet
              </Typography>
              <ToggleButtonGroup size="small" exclusive value={settings.density}
                onChange={(e, v) => v && set({ density: v, autoFit: false })}
                sx={{ mb: 1, display: 'flex' }}>
                {DENSITY_ORDER.map(key => (
                  <ToggleButton key={key} value={key}>{DENSITIES[key].label}</ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Tooltip title="Byter till tät täthet och mindre text, och kryssar vid behov bort profil och bransch. Projektinfo, mjukvaror och tillval du själv kryssat i tas aldrig bort.">
                <FormControlLabel
                  control={<Checkbox size="small" checked={settings.autoFit}
                    onChange={e => set({ autoFit: e.target.checked })} />}
                  label={<Typography variant="caption">Auto-anpassa till {settings.pages} sidor</Typography>}
                  sx={{ display: 'block', m: 0 }}
                />
              </Tooltip>
              <Tooltip title="BAS-P/BAS-U, säkerhet på väg, heta arbeten m.fl. bryts ut till en egen rad som aldrig auto-kryssas bort. Rekommenderas när behörigheter är skall-krav.">
                <FormControlLabel
                  control={<Checkbox size="small" checked={settings.splitLicences}
                    onChange={e => set({ splitLicences: e.target.checked })} />}
                  label={<Typography variant="caption">Behörigheter alltid med</Typography>}
                  sx={{ display: 'block', m: 0 }}
                />
              </Tooltip>
              <FormControlLabel
                control={<Checkbox size="small" checked={settings.includeToolsBasic}
                  onChange={e => set({ includeToolsBasic: e.target.checked })} />}
                label={<Typography variant="caption">Ta med grundläggande IT-kunskaper</Typography>}
                sx={{ display: 'block', m: 0 }}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={settings.zebra}
                  onChange={e => set({ zebra: e.target.checked })} />}
                label={<Typography variant="caption">Varannan rad tonad</Typography>}
                sx={{ display: 'block', m: 0 }}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={settings.showBudget}
                  onChange={e => set({ showBudget: e.target.checked })} />}
                label={<Typography variant="caption">Projektbudget som kolumn</Typography>}
                sx={{ display: 'block', m: 0 }}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={settings.showLogo}
                  onChange={e => set({ showLogo: e.target.checked })} />}
                label={<Typography variant="caption">Företagslogotyp</Typography>}
                sx={{ display: 'block', m: 0 }}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={settings.showPhoto}
                  onChange={e => set({ showPhoto: e.target.checked })} />}
                label={<Typography variant="caption">Profilbild</Typography>}
                sx={{ display: 'block', m: 0 }}
              />
              <Tooltip title="Bilden läggs i det utfällda blocket, rakt under projektnamnet, med beskrivningen bredvid. Höjden absorberas då av texten i stället för att läggas till den.">
                <FormControlLabel
                  control={<Checkbox size="small" checked={settings.showAssignmentImages}
                    onChange={e => set({ showAssignmentImages: e.target.checked })} />}
                  label={<Typography variant="caption">Uppdragsbilder i utfällda rader</Typography>}
                  sx={{ display: 'block', m: 0 }}
                />
              </Tooltip>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ minWidth: 190 }}>
              <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                Passform
              </Typography>
              <FitStatus fit={fit} settings={settings} />

              {settings.autoNote && (
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: .5 }}>
                  Auto-anpassade: {settings.autoNote}
                </Typography>
              )}

              {suggestion && (
                <Button size="small" sx={{ mt: .5, px: 0 }}
                  onClick={() => toggleOptional(suggestion.id)}>
                  + {suggestion.label} ({suggestion.cost} rader)
                </Button>
              )}

              {fit.wrappedRows > 0 && (
                <Box sx={{ mt: .5 }}>
                  <Typography variant="caption"
                    sx={{ color: '#FF6B00', display: 'flex', gap: .5, alignItems: 'center' }}>
                    <WarningAmberIcon sx={{ fontSize: 14 }} />
                    {fit.wrappedRows} rad{fit.wrappedRows === 1 ? '' : 'er'} radbryter
                  </Typography>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={settings.markWrapped}
                      onChange={e => set({ markWrapped: e.target.checked })} />}
                    label={<Typography variant="caption">Visa var</Typography>}
                    sx={{ display: 'block', m: 0 }}
                  />
                </Box>
              )}

              <Typography variant="caption" display="block"
                sx={{ color: 'text.secondary', mt: .5 }}>
                {expandedCount > 0
                  ? `${expandedCount} uppdrag utfällda`
                  : 'Klicka en projektrad för att fälla ut beskrivningen'}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
}

function OptionRow({ checked, onChange, label, cost }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <FormControlLabel
        control={<Checkbox size="small" checked={checked} onChange={onChange} />}
        label={<Typography variant="caption">{label}</Typography>}
        sx={{ m: 0 }}
      />
      <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {cost > 0 ? `+${cost} rader` : '–'}
      </Typography>
    </Box>
  );
}

function FitStatus({ fit, settings }) {
  if (!fit.heightMm) return <Typography variant="caption">Mäter…</Typography>;

  if (fit.overflowRows > 0) {
    return (
      <Chip size="small" color="error" variant="outlined"
        label={`${fit.overflowRows} rader för mycket`} />
    );
  }
  return (
    <Box>
      <Chip size="small" color="success" variant="outlined"
        label={`Får plats på ${settings.pages} sidor`} />
      <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: .5 }}>
        {fit.freeRows} rader kvar
      </Typography>
    </Box>
  );
}
